// src/pages/league-dashboard/FreeAgentSearch.js - REFACTORED WITH CUSTOM HOOKS
import React, { useCallback, useState, useEffect } from 'react';
// 9o.6.d-followup: useNavigate removed — both single-add (L323) and bulk-add (L447) navigates
// dropped (restored pre-Sep-2025 + mobile-parity behavior; stay on FA page after add).
import { RefreshCw } from 'lucide-react';
import TeamPageHeader from '../../components/common/TeamPageHeader';
import { dynastyTheme } from '../../services/colorService';
import { leaguesAPI } from '../../services/apiService';
import { formatApiError } from '../../services/errorUtils';
import { useCommissioner } from '../../contexts/CommissionerContext';
import { usePlayerModal } from '../../contexts/PlayerModalContext';
import { useBatchSelection } from './free-agent-search/BatchSelectionProvider';
import { 
  canAddPlayers,
  findBestPositionForPlayer
} from '../../utils/RosterCapacityUtils';

// Import custom hooks
import {
  useSearchFilters,
  useLeagueContext,
  usePlayerData,
  usePlayerAssignment,
  useRosterPlan
} from './free-agent-search/hooks';

// Import modular components
import BatchSelectionProvider from './free-agent-search/BatchSelectionProvider';
import SearchControls from './free-agent-search/SearchControls';
import StatusBanners from './free-agent-search/StatusBanners';
import VirtualPlayerTable from './free-agent-search/VirtualPlayerTable';
import CommissionerToggle from '../../components/commissioner/CommissionerToggle';
import CommissionerModeBar from '../../components/commissioner/CommissionerModeBar';
import PositionAssignmentDropdown from '../../components/league-dashboard/PositionAssignmentDropdown';
import RosterPlanModal from '../../components/league-dashboard/RosterPlanModal';
import BulkPositionAssignmentModal from '../../components/league-dashboard/BulkPositionAssignmentModal';
import DropDesignationModal from '../../components/modals/DropDesignationModal';

// 4-branch contract resolution per Bible § 4.5 contract model.
// Extracted as a pure named export per Bible §14 frontend testing
// convention so unit tests can target the four branches directly:
//   1. Minors: zero out (salary=0, years=0)
//   2. Offseason w/ projected_contract: keeper rights from
//      projected_contract.baseline_price + projected_contract.contract_years
//   3. Owned in-season: existing league_context.financial.{contract_salary,
//      contract_years}
//   4. FA pickup (default): league_context.financial.baseline_price +
//      league.salary_settings.standard_contract_length
// Throws per Bible Rule 9 on missing required data (no silent fallback).
export const resolveContractForAdd = (player, leagueStatus, league, assignment) => {
  const isOffseason = ['setup', 'draft', 'finalizing'].includes(leagueStatus);
  const isOwnedAlready = player.league_context?.status === 'owned';
  const isMinors = assignment.type === 'minors';
  const playerName = player.info?.full_name;

  if (isMinors) {
    return { playerSalary: 0, contractYears: 0 };
  }

  if (isOffseason && player.projected_contract) {
    const playerSalary = player.projected_contract.baseline_price;
    const contractYears = player.projected_contract.contract_years;
    if (playerSalary == null) {
      throw new Error(`Cannot add ${playerName}: missing projected_contract.baseline_price`);
    }
    if (contractYears == null) {
      throw new Error(`Cannot add ${playerName}: missing projected_contract.contract_years`);
    }
    return { playerSalary, contractYears };
  }

  if (isOwnedAlready) {
    const playerSalary = player.league_context.financial?.contract_salary;
    const contractYears = player.league_context.financial?.contract_years;
    if (playerSalary == null) {
      throw new Error(`Cannot add ${playerName}: missing league_context.financial.contract_salary on owned player`);
    }
    if (contractYears == null) {
      throw new Error(`Cannot add ${playerName}: missing league_context.financial.contract_years on owned player`);
    }
    return { playerSalary, contractYears };
  }

  // FA pickup default. Bible § 4.5.1 line 809 + § 10 line 1701: FA pickup
  // price = baseline_price. Bible § 4.5.2 line 821: contract_years =
  // league.salary_settings.standard_contract_length.
  const playerSalary = player.league_context?.financial?.baseline_price;
  const contractYears = league?.salary_settings?.standard_contract_length;
  if (playerSalary == null) {
    throw new Error(
      `Cannot add ${playerName}: missing baseline_price on league_context.financial. ` +
      `Pricing engine may not have run for this league.`
    );
  }
  if (contractYears == null) {
    throw new Error(
      `Cannot add ${playerName}: league.salary_settings.standard_contract_length is not configured.`
    );
  }
  return { playerSalary, contractYears };
};

// ========================================
// WRAPPER COMPONENT WITH BATCH PROVIDER
// ========================================
const FreeAgentSearch = ({ leagueId, onPlayerAdded, league, user, userTeam }) => {
  return (
    <BatchSelectionProvider>
      <FreeAgentSearchInner
        leagueId={leagueId}
        onPlayerAdded={onPlayerAdded}
        league={league}
        user={user}
        userTeam={userTeam}
      />
    </BatchSelectionProvider>
  );
};

// ========================================
// INNER COMPONENT WITH CUSTOM HOOKS
// ========================================
const FreeAgentSearchInner = ({ leagueId, onPlayerAdded, league, user, userTeam }) => {
  const { openPlayerModal } = usePlayerModal();
  const { 
    isCommissionerMode, 
    activeTeamId,
    activeTeamName, 
    getTargetTeamId,
    isCommissioner 
  } = useCommissioner();
  
  // Access batch selection context
  const { getSelectedPlayerObjects, clearAllSelections } = useBatchSelection();
  
  // ========================================
  // CUSTOM HOOKS - REPLACES 32 useState CALLS
  // ========================================
  const searchFilters = useSearchFilters({ includeNon40Man: !!isCommissionerMode });
  const leagueContext = useLeagueContext(leagueId, isCommissionerMode);

  // Sync includeNon40Man when Commissioner Mode is toggled while on page
  useEffect(() => {
    searchFilters.setIncludeNon40Man(!!isCommissionerMode);
  }, [isCommissionerMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pass search filters to player data hook (statsYear determines is_historical on backend).
  // DD-FASEARCH-RACE-0730: isCommissionerMode is passed as the AUTHORITATIVE source for
  // include_non_40man so the fetch never depends on the render-late setIncludeNon40Man effect
  // above (which lags one render behind the toggle).
  const playerData = usePlayerData(leagueId, searchFilters, leagueContext, isCommissionerMode);
  const positionAssignment = usePlayerAssignment(leagueId, league, {
    isCommissionerMode,
    activeTeamName,
    getTargetTeamId,
    transactionsEnabled: leagueContext.transactionsEnabled,
    leagueStatus: leagueContext.leagueStatus,
    user,
  });

  // Phase 2.2: drop-shuffle plan flow.
  // Per ROSTER_PLAN_SPEC.md § B.5.3. Owns API calls + state machine
  // for /plan-add and /execute-plan. Modal is render-only.
  const rosterPlan = useRosterPlan({
    leagueId,
    candidate: positionAssignment.playerForAssignment,
    currentRoster: positionAssignment.currentRoster,
    isCommissionerMode,
    getTargetTeamId,
  });

  const [showDropModal, setShowDropModal] = useState(false);
  const [playerPendingAdd, setPlayerPendingAdd] = useState(null);
  const [designatedDropId, setDesignatedDropId] = useState(null);
  // Phase A.4.5: waiver-claim queue mode (parity with mobile). When true, the
  // RosterPlanModal's onConfirm submits a waiver claim with the chosen plan
  // instead of executing the plan immediately.
  const [isWaiverPlanQueueMode, setIsWaiverPlanQueueMode] = useState(false);
  const [waiverPlayerForQueue, setWaiverPlayerForQueue] = useState(null);

  const handleWaiverRequestPlan = useCallback((waiverPlayer) => {
    if (!waiverPlayer) return;
    setWaiverPlayerForQueue(waiverPlayer);
    positionAssignment.setPlayerForAssignment(waiverPlayer);
    setIsWaiverPlanQueueMode(true);
    rosterPlan.open();
  }, [positionAssignment, rosterPlan]);

  const handleWaiverPlanQueueConfirm = useCallback(async () => {
    if (!waiverPlayerForQueue || !rosterPlan.selectedPlan || !rosterPlan.selectedDropPlayer) {
      return null;
    }
    const leaguePlayerId = waiverPlayerForQueue.ids?.league_player;
    const dropLeaguePlayerId = rosterPlan.selectedDropPlayer.ids?.league_player;
    const planCandidateMlb = waiverPlayerForQueue.ids?.mlb;
    const planDropMlb = rosterPlan.selectedDropPlayer.ids?.mlb;
    if (!leaguePlayerId || !dropLeaguePlayerId || planCandidateMlb == null || planDropMlb == null) {
      playerData.setError('Cannot queue plan: missing identifiers on waiver or drop player.');
      setTimeout(() => playerData.setError(''), 5000);
      return null;
    }
    try {
      const res = await leaguesAPI.submitWaiverClaim(leagueId, {
        player_league_id: leaguePlayerId,
        drop_player_league_id: dropLeaguePlayerId,
        plan_fingerprint: rosterPlan.selectedPlan.plan_fingerprint,
        plan_candidate_player_id: planCandidateMlb,
        plan_drop_player_id: planDropMlb,
      });
      const data = res?.data || res;
      if (data?.success) {
        const playerName = waiverPlayerForQueue.info?.full_name || 'player';
        playerData.setSuccessMessage(`Waiver claim submitted for ${playerName}.`);
        setTimeout(() => playerData.setSuccessMessage(''), 5000);
      } else {
        const msg = data?.message || data?.error || 'Failed to submit waiver claim.';
        playerData.setError(msg);
        setTimeout(() => playerData.setError(''), 6000);
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to submit waiver claim.';
      playerData.setError(msg);
      setTimeout(() => playerData.setError(''), 6000);
    }
    rosterPlan.reset();
    positionAssignment.setPlayerForAssignment(null);
    setIsWaiverPlanQueueMode(false);
    setWaiverPlayerForQueue(null);
    return { success: true };
  }, [waiverPlayerForQueue, rosterPlan, positionAssignment, leagueId, playerData]);

  // ========================================
  // PLAYER ACTION HANDLERS
  // ========================================
  const handleAddPlayer = useCallback(async (player) => {
    // Check if transactions are allowed
    const isOfflineDraft = leagueContext.leagueStatus === 'offline_draft';
    const isDraftMode = ['draft', 'offline_draft'].includes(leagueContext.leagueStatus);
    
    // Allow transactions in offline draft mode, commissioner mode, or when normally enabled
    if (!isOfflineDraft && !isDraftMode && !isCommissionerMode && !leagueContext.transactionsEnabled) {
      playerData.setError('Transactions are not allowed until the commissioner sets player prices');
      setTimeout(() => playerData.setError(''), 5000);
      return;
    }

    // Check ownership using canonical structure
    const isOwned = player.league_context?.team?.team_name;
    if (isOwned) {
      playerData.setError('Player is already owned by a team');
      setTimeout(() => playerData.setError(''), 5000);
      return;
    }

    if (!positionAssignment.rosterCapacityAnalysis) {
      await positionAssignment.loadRosterCapacity();
      playerData.setError('Checking roster capacity...');
      setTimeout(() => playerData.setError(''), 3000);
      return;
    }

    const capacityCheck = canAddPlayers(1, positionAssignment.rosterCapacityAnalysis);
    if (!capacityCheck.canAdd) {
      setPlayerPendingAdd(player);
      setShowDropModal(true);
      return;
    }

    const suggestion = findBestPositionForPlayer(player, positionAssignment.rosterCapacityAnalysis, league);
    
    if (!suggestion.hasSuggestions) {
      const firstName = player.info?.first_name;
      const lastName = player.info?.last_name;
      playerData.setError(`No available roster slots for ${firstName} ${lastName}`);
      setTimeout(() => playerData.setError(''), 5000);
      return;
    }

    // Always show modal for user confirmation (with best position pre-selected)
    positionAssignment.setPlayerForAssignment(player);
    positionAssignment.setShowPositionModal(true);
  }, [leagueContext.transactionsEnabled, leagueContext.leagueStatus, isCommissionerMode, positionAssignment, league, playerData]);

  const executePlayerAssignment = useCallback(async (player, assignment) => {
    const firstName = player.info?.first_name;
    const lastName = player.info?.last_name;
    const leaguePlayerId = player.ids?.league_player;

    positionAssignment.setAddingPlayer(leaguePlayerId);
    playerData.setError('');

    try {
      // Bible Rule 9: missing required data is a bug — throw, not fallback.
      if (!leaguePlayerId) {
        throw new Error(
          `Cannot add player: missing ids.league_player (mlb=${player.ids?.mlb}, ` +
          `name=${player.info?.full_name}). Player object likely came from non-canonical source.`
        );
      }
      if (!assignment?.type) {
        throw new Error('Cannot add player: assignment.type is required');
      }
      if (assignment.type === 'active' && !assignment.slotId) {
        throw new Error(
          `Cannot add ${player.info?.full_name} to active roster: missing assignment.slotId`
        );
      }

      // 4-branch contract resolution delegated to resolveContractForAdd
      // (named export above). Throws per Bible Rule 9 on missing data.
      const { playerSalary, contractYears } = resolveContractForAdd(
        player,
        leagueContext.leagueStatus,
        league,
        assignment
      );
      const isMinors = assignment.type === 'minors';

      const playerDataPayload = {
        league_player_id: leaguePlayerId,
        salary: playerSalary,
        contract_years: contractYears,
        roster_status: assignment.type,
        roster_position: assignment.type === 'active' ? assignment.slotId : null,
        start_contract: !isMinors,
        drop_player_id: designatedDropId || null
      };

      if (isCommissionerMode) {
        playerDataPayload.commissioner_action = true;
        playerDataPayload.target_team_id = getTargetTeamId(null);
      }

      const response = await leaguesAPI.addPlayerToTeam(leagueId, playerDataPayload);

      if (response.success) {
        const teamName = isCommissionerMode ? activeTeamName : 'your team';
        const positionText = assignment.type === 'active' ? assignment.position.toUpperCase() : assignment.type;
        playerData.setSuccessMessage(`${response.player_name || firstName + ' ' + lastName} added to ${teamName} (${positionText})!`);
        // 9o.6.d-followup completion: 3s auto-clear matches mobile + matches MyRoster.js pattern.
        // Pre-9o.6.d the navigate handled implicit cleanup; without navigate the message
        // persisted indefinitely (per success-message investigation).
        setTimeout(() => playerData.setSuccessMessage(''), 3000);

        if (!searchFilters.showAll) {
          playerData.setPlayers(prev => prev.filter(p => {
            const pId = p.ids?.league_player;
            return pId !== leaguePlayerId;
          }));
          playerData.setTotalCount(prev => prev - 1);
        } else {
          playerData.loadPlayers(0, false);
        }
        
        await positionAssignment.loadRosterCapacity();
        
        window.dispatchEvent(new Event('roster-updated'));
        
        if (onPlayerAdded) {
          onPlayerAdded(player);
        }
        // 9o.6.d-followup: removed setTimeout(navigate, 1000) → /leagues/{id} my-roster.
        // Pre-Sep-2025 behavior + mobile parity: stay on FA page after add. The success message
        // + optimistic list filter + roster-updated event provide adequate confirmation; the
        // forced navigation broke bulk-add workflows (user had to re-open FAs after every add).
        // See 9o.6.d-followup investigation report for the full rationale + git history.

      } else {
        const errorMsg = response.message || response.error?.message || response.error || 'Failed to add player';
        playerData.setError(errorMsg);
      }
    } catch (err) {
      // 9o.6.d-followup: formatApiError replaces inline JSON.stringify + cargo-cult
      // err.response.data.message check. Now handles plain HTTPException routes correctly
      // (was producing raw JSON blobs in banner).
      playerData.setError(formatApiError(err, 'Failed to add player'));
    } finally {
      positionAssignment.setAddingPlayer(null);
      positionAssignment.setShowPositionModal(false);
      positionAssignment.setPlayerForAssignment(null);
      setDesignatedDropId(null);
      setPlayerPendingAdd(null);
    }
  }, [isCommissionerMode, activeTeamName, getTargetTeamId, leagueId, searchFilters.showAll, playerData, positionAssignment, onPlayerAdded, activeTeamId, user, designatedDropId]);

  const handleBatchAddPlayers = useCallback(async (selectedPlayers) => {
    if (!leagueContext.transactionsEnabled) {
      playerData.setError('Transactions are not allowed until prices are set');
      return;
    }

    if (selectedPlayers.length === 0) {
      playerData.setError('No players selected');
      return;
    }

    if (!positionAssignment.rosterCapacityAnalysis) {
      await positionAssignment.loadRosterCapacity();
      playerData.setError('Checking roster capacity...');
      setTimeout(() => playerData.setError(''), 3000);
      return;
    }

    const capacityCheck = canAddPlayers(selectedPlayers.length, positionAssignment.rosterCapacityAnalysis);
    if (!capacityCheck.canAdd) {
      playerData.setError(`Cannot add ${selectedPlayers.length} players: ${capacityCheck.message}`);
      setTimeout(() => playerData.setError(''), 8000);
      return;
    }

    positionAssignment.setShowBulkAssignmentModal(true);
  }, [leagueContext.transactionsEnabled, positionAssignment, playerData]);

  const handleBulkAssignmentComplete = useCallback(async (assignmentData) => {
    playerData.setError('');
    
    const results = {
      successful: [],
      failed: []
    };

    try {
      for (const assignment of assignmentData) {
        try {
          const leaguePlayerId = assignment.player.ids?.league_player;
          const playerDataPayload = {
            league_player_id: leaguePlayerId,
            salary: assignment.salary,
            contract_years: assignment.contract_years,
            roster_status: assignment.roster_status,
            roster_position: assignment.roster_position,
            start_contract: assignment.start_contract
          };

          if (isCommissionerMode) {
            playerDataPayload.commissioner_action = true;
            playerDataPayload.target_team_id = getTargetTeamId(null);
          }

          const response = await leaguesAPI.addPlayerToTeam(leagueId, playerDataPayload);

          if (response.success) {
            const firstName = assignment.player.info?.first_name;
            const lastName = assignment.player.info?.last_name;
            results.successful.push({
              player: assignment.player,
              name: response.player_name || `${firstName} ${lastName}`,
              position: assignment.roster_status === 'active' ? assignment.roster_position : assignment.roster_status
            });
          } else {
            const firstName = assignment.player.info?.first_name;
            const lastName = assignment.player.info?.last_name;
            results.failed.push({
              player: assignment.player,
              name: `${firstName} ${lastName}`,
              error: response.message || 'Unknown error'
            });
          }
        } catch (playerErr) {
          const firstName = assignment.player.info?.first_name;
          const lastName = assignment.player.info?.last_name;
          results.failed.push({
            player: assignment.player,
            name: `${firstName} ${lastName}`,
            error: playerErr.message || 'Network error'
          });
        }
      }

      if (results.successful.length > 0) {
        const teamName = isCommissionerMode ? activeTeamName : 'your team';
        playerData.setSuccessMessage(`Successfully added ${results.successful.length} players to ${teamName}!`);
        // 9o.6.d-followup completion: 3s auto-clear (bulk variant) matches mobile + MyRoster.js.
        setTimeout(() => playerData.setSuccessMessage(''), 3000);

        clearAllSelections();

        await positionAssignment.loadRosterCapacity();
        window.dispatchEvent(new Event('roster-updated'));

        // 9o.6.d-followup (bulk variant): removed navigate → /leagues/{id} my-roster.
        // Same rationale as single-add removal: bulk-add specifically benefits from staying on
        // the FA list (user just confirmed they wanted to add MANY players; teleporting them
        // away breaks subsequent bulk work). Success message + clearAllSelections + roster-updated
        // event + loadRosterCapacity already provide adequate confirmation.
      }

      if (results.failed.length > 0) {
        const failedNames = results.failed.map(f => f.name).join(', ');
        playerData.setError(`Failed to add: ${failedNames}`);
      }

    } catch (err) {
      console.error('Error in bulk assignment completion:', err);
      // 9o.6.d-followup: was hardcoded 'Bulk assignment failed' regardless of actual cause.
      // formatApiError now surfaces backend detail when present.
      playerData.setError(formatApiError(err, 'Bulk assignment failed'));
    } finally {
      positionAssignment.setShowBulkAssignmentModal(false);
    }
  }, [isCommissionerMode, activeTeamName, getTargetTeamId, leagueId, clearAllSelections, positionAssignment, playerData]);

  const handlePlayerClick = useCallback((player) => {
    const playerId = player.ids?.mlb;
    openPlayerModal(playerId, player);
  }, [openPlayerModal]);

  // ========================================
  // LOADING STATE
  // ========================================
  if (!leagueContext.isReady) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center gap-3">
            <RefreshCw className={`w-6 h-6 animate-spin ${dynastyTheme.classes.text.primary}`} />
            <span className={dynastyTheme.classes.text.white}>
              Loading league settings and roster capacity...
            </span>
          </div>
        </div>
      </div>
    );
  }

  // ========================================
  // STATE OBJECT FOR CHILD COMPONENTS
  // ========================================
  const freeAgentState = {
    // Player Data Hook
    players: playerData.players,
    totalCount: playerData.totalCount,
    loading: playerData.loading,
    backgroundLoading: playerData.backgroundLoading,
    hasMore: playerData.hasMore,
    page: playerData.page,
    error: playerData.error,
    setError: playerData.setError,
    successMessage: playerData.successMessage,
    setSuccessMessage: playerData.setSuccessMessage,
    loadPlayers: playerData.loadPlayers,
    loadMore: playerData.loadMore,
    allDataLoaded: playerData.allDataLoaded,
    
    // Search Filters Hook
    searchTerm: searchFilters.searchTerm,
    setSearchTerm: searchFilters.setSearchTerm,
    activeTab: searchFilters.activeTab,
    setActiveTab: searchFilters.setActiveTab,
    positionFilter: searchFilters.positionFilter,
    setPositionFilter: searchFilters.setPositionFilter,
    viewMode: searchFilters.viewMode,
    setViewMode: searchFilters.setViewMode,
    showAll: searchFilters.showAll,
    setShowAll: searchFilters.setShowAll,
    advancedFilters: searchFilters.advancedFilters,
    setAdvancedFilters: searchFilters.setAdvancedFilters,
    statsYear: searchFilters.statsYear,
    // Recent toggle — rolling-row visibility.
    showRecent: searchFilters.showRecent,
    setShowRecent: searchFilters.setShowRecent,
    currentSeason: league?.current_season,
    
    // League Context Hook
    leagueStatus: leagueContext.leagueStatus,
    transactionsEnabled: leagueContext.transactionsEnabled,
    browseMode: leagueContext.browseMode,
    noPricesWarning: leagueContext.noPricesWarning,
    pricesExist: leagueContext.pricesExist,
    savedPrices: leagueContext.savedPrices,
    handleBrowseAnyway: leagueContext.handleBrowseAnyway,
    checkLeagueStatus: leagueContext.checkLeagueStatus,
    loadSavedPrices: leagueContext.loadSavedPrices,
    
    // Position Assignment Hook
    addingPlayer: positionAssignment.addingPlayer,
    rosterCapacityAnalysis: positionAssignment.rosterCapacityAnalysis,
    loadRosterCapacity: positionAssignment.loadRosterCapacity,
    
    // Action Handlers
    handlePlayerClick,
    handleAddPlayer,
    handleBatchAddPlayers,
    onWaiverRequestPlan: handleWaiverRequestPlan,
  };

  // ========================================
  // MAIN COMPONENT RENDER
  // ========================================
  return (
    <div className="space-y-6">
      <TeamPageHeader
        title="Free Agent Search"
        logoUrl={userTeam?.team_logo_url}
        teamName={userTeam?.team_name}
        teamColors={userTeam?.team_colors}
        teamMotto={userTeam?.team_motto}
        badges={false}
        actions={
          <>
            <CommissionerModeBar
              league={league}
              onTeamSwitch={() => {
                if (leagueContext.transactionsEnabled || leagueContext.browseMode) {
                  playerData.loadPlayers(0, false);
                  positionAssignment.loadRosterCapacity();
                }
              }}
            />
            <button
              onClick={() => {
                leagueContext.checkLeagueStatus();
                leagueContext.loadSavedPrices();
                positionAssignment.loadRosterCapacity();
                playerData.loadPlayers(0, false);
              }}
              disabled={playerData.loading}
              className={`${dynastyTheme.utils.getComponent('button', 'secondary', 'sm')} flex items-center gap-2 disabled:opacity-50`}
            >
              <RefreshCw className={`w-4 h-4 ${playerData.loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </>
        }
      />

      {/* Status Banners */}
      {leagueContext.isReady && !leagueContext.transactionsEnabled && !leagueContext.browseMode && !positionAssignment.addingPlayer && !playerData.loading && (
        <StatusBanners 
          state={freeAgentState}
          isCommissionerMode={isCommissionerMode}
          activeTeamName={activeTeamName}
        />
      )}

      {/* Main Content */}
      {(leagueContext.transactionsEnabled || leagueContext.browseMode) && (
        <>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              {/* Season Stats Toggle */}
              <div className="flex items-center gap-2">
                <span className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>Stats:</span>
                <div className="flex gap-1">
                  <button
                    onClick={() => searchFilters.setStatsYear('current')}
                    className={`px-3 py-1 rounded text-xs font-medium transition-all
                      ${searchFilters.statsYear === 'current'
                        ? `${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.text.dark}`
                        : `${dynastyTheme.classes.bg.neutralDarker} ${dynastyTheme.classes.text.neutralLight} hover:bg-gray-700`
                      }`}
                  >
                    {league?.stats_season} Stats
                  </button>
                  <button
                    onClick={() => searchFilters.setStatsYear('previous')}
                    className={`px-3 py-1 rounded text-xs font-medium transition-all
                      ${searchFilters.statsYear === 'previous'
                        ? `${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.text.dark}`
                        : `${dynastyTheme.classes.bg.neutralDarker} ${dynastyTheme.classes.text.neutralLight} hover:bg-gray-700`
                      }`}
                  >
                    {league?.current_season} Stats
                  </button>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {leagueContext.transactionsEnabled && leagueContext.leagueStatus === 'in_season' && (
                <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">
                  In Season
                </span>
              )}
              {leagueContext.transactionsEnabled && leagueContext.leagueStatus === 'draft' && (
                <span className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm">
                  Draft Mode
                </span>
              )}
              
              {/* Roster Capacity Indicator */}
              {positionAssignment.rosterCapacityAnalysis && (
                <div className={`px-3 py-1 rounded-full text-sm ${
                  positionAssignment.rosterCapacityAnalysis.availableSlots > 5 
                    ? 'bg-green-500/20 text-green-400'
                    : positionAssignment.rosterCapacityAnalysis.availableSlots > 0
                    ? 'bg-yellow-500/20 text-yellow-400' 
                    : 'bg-red-500/20 text-red-400'
                }`}>
                  {positionAssignment.rosterCapacityAnalysis.availableSlots} roster slots
                </div>
              )}
              
              {/* Commissioner Toggle - Shows if user is commissioner and NOT in commissioner mode */}
              <CommissionerToggle
                leagueId={leagueId}
                userIsCommissioner={league?.role === 'commissioner' || isCommissioner}
              />
            </div>
          </div>

          <SearchControls 
            state={freeAgentState}
            leagueId={leagueId}
            isCommissionerMode={isCommissionerMode}
            activeTeamName={activeTeamName}
          />

          {/* VirtualPlayerTable - M2 design + react-window virtualization */}
          <VirtualPlayerTable
            state={freeAgentState}
            leagueId={leagueId}
            isCommissionerMode={isCommissionerMode}
            activeTeamName={activeTeamName}
            activeTeamId={activeTeamId}
          />
        </>
      )}

      {/* POSITION ASSIGNMENT MODALS */}

      {/* Drop Designation Modal — shown when roster is full on FA add */}
      <DropDesignationModal
        isOpen={showDropModal}
        onClose={() => {
          setShowDropModal(false);
          setPlayerPendingAdd(null);
          setDesignatedDropId(null);
        }}
        onConfirm={(dropPlayerId) => {
          setDesignatedDropId(dropPlayerId);
          setShowDropModal(false);
          positionAssignment.setPlayerForAssignment(playerPendingAdd);
          positionAssignment.setShowPositionModal(true);
        }}
        playerBeingAdded={playerPendingAdd}
        currentRoster={positionAssignment.currentRoster || []}
        leagueId={leagueId}
        context="fa_add"
        isRosterFull={positionAssignment.rosterCapacityAnalysis ? !canAddPlayers(1, positionAssignment.rosterCapacityAnalysis).canAdd : true}
        waiverPriority={null}
        totalTeams={null}
        waiverExpiresAt={null}
      />

      {/* Single Player Position Assignment */}
      <PositionAssignmentDropdown
        player={positionAssignment.playerForAssignment}
        league={league}
        currentRoster={positionAssignment.currentRoster}
        onAssign={(assignmentData) => executePlayerAssignment(assignmentData.player, {
          position: assignmentData.assignment === 'bench' ? 'bench' :
                   assignmentData.assignment === 'minors' ? 'minors' :
                   assignmentData.assignment.split('_')[0],
          slotId: assignmentData.assignment,
          type: assignmentData.roster_status,
          reason: 'Manual selection',
          priority: 1
        })}
        onCancel={() => {
          positionAssignment.setShowPositionModal(false);
          positionAssignment.setPlayerForAssignment(null);
        }}
        isVisible={positionAssignment.showPositionModal}
        // Phase 2.2: 5th button "Drop a player" entry point.
        // Per ROSTER_PLAN_SPEC.md § B.5.1 patched condition. Closes
        // this dropdown and opens RosterPlanModal. The dropdown only
        // renders the button when (active full + bench/DL/minors has
        // room).
        onOpenDropShuffle={() => {
          positionAssignment.setShowPositionModal(false);
          rosterPlan.open();
        }}
      />

      {/* Phase 2.2: Drop-shuffle plan modal. Per § B.5.2. */}
      <RosterPlanModal
        candidate={positionAssignment.playerForAssignment}
        currentRoster={positionAssignment.currentRoster}
        state={rosterPlan.state}
        plans={rosterPlan.plans}
        selectedDropPlayer={rosterPlan.selectedDropPlayer}
        selectedPlan={rosterPlan.selectedPlan}
        errorClass={rosterPlan.errorClass}
        errorMessage={rosterPlan.errorMessage}
        infeasibleReason={rosterPlan.infeasibleReason}
        onPickDrop={rosterPlan.requestPlans}
        onChangeDrop={rosterPlan.changeDrop}
        onSelectPlan={rosterPlan.selectPlan}
        onConfirm={isWaiverPlanQueueMode ? handleWaiverPlanQueueConfirm : rosterPlan.executePlan}
        onCancel={() => {
          rosterPlan.cancel();
          rosterPlan.reset();
          positionAssignment.setPlayerForAssignment(null);
          if (isWaiverPlanQueueMode) {
            setIsWaiverPlanQueueMode(false);
            setWaiverPlayerForQueue(null);
          }
        }}
        onRetry={rosterPlan.retry}
        onDone={() => {
          // After successful execution, close cleanly.
          // executor's roster-updated dispatch (in useRosterPlan)
          // triggers usePlayerAssignment's listener → roster refetch.
          rosterPlan.reset();
          positionAssignment.setPlayerForAssignment(null);
          if (isWaiverPlanQueueMode) {
            setIsWaiverPlanQueueMode(false);
            setWaiverPlayerForQueue(null);
          }
        }}
        isVisible={
          rosterPlan.state !== 'idle' && rosterPlan.state !== 'cancelled'
        }
      />

      {/* Bulk Player Position Assignment */}
      <BulkPositionAssignmentModal
        selectedPlayers={getSelectedPlayerObjects(playerData.players)}
        league={league}
        capacityAnalysis={positionAssignment.rosterCapacityAnalysis}
        currentRoster={positionAssignment.currentRoster}
        onAssignAll={handleBulkAssignmentComplete}
        onCancel={() => positionAssignment.setShowBulkAssignmentModal(false)}
        isVisible={positionAssignment.showBulkAssignmentModal}
      />
    </div>
  );
};

export default FreeAgentSearch;
